import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/onboarding-context';
import { useAuth } from '../../contexts/auth/auth-context';
import { Onboarding } from './onboarding';
import { OnboardingReady } from './ready/ready';
import { OnboardingReview } from './review/review';

const POLL_INTERVAL_MS = 10_000; // check every 10 seconds

const OnboardingContent: React.FC = () => {
  const { hasQuestions, totalQuestions, onboardingBootLoading, refreshQuestions } = useOnboarding();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const reallyHasQuestions = (totalQuestions ?? 0) > 0 || hasQuestions;

  // Poll for questions while on the welcome screen (no questions yet)
  useEffect(() => {
    if (onboardingBootLoading || reallyHasQuestions) {
      // No need to poll — either still loading or questions already exist
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Start polling
    pollingRef.current = setInterval(() => {
      refreshQuestions();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [onboardingBootLoading, reallyHasQuestions, refreshQuestions]);

  if (onboardingBootLoading) {
    return (
      <div style={{ background: '#1A1A1C', height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.15)', borderTopColor: '#5E84ED', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (reallyHasQuestions) {
    return <OnboardingReady />;
  }

  return <Onboarding />;
};

const OnboardingReviewWrapper: React.FC = () => {
  const { isCompleted } = useOnboarding();
  if (!isCompleted) return <Navigate to="/onboarding" replace />;
  return <OnboardingReview />;
};

const OnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const onboardingCompleted = profile?.onboarding_completed === true;

  // Only redirect if profile shows onboarding is completed
  // (profile is updated after user clicks Done and navigates)
  if (onboardingCompleted) {
    return <Navigate to="/chat" replace />;
  }

  return <>{children}</>;
};

export const OnboardingRouter: React.FC = () => {
  return (
    <OnboardingGate>
      <Routes>
        <Route path="/review" element={<OnboardingReviewWrapper />} />
        <Route path="/*" element={<OnboardingContent />} />
      </Routes>
    </OnboardingGate>
  );
};
